import { z } from 'zod';
import { hvacStateSchema, powertrainTypeSchema, routeTypeSchema, runStatusSchema, seasonSchema, testTypeSchema, timeBandSchema, trafficLevelSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { compatibilityOnFailSchema, compatibilityOperatorSchema } from '../../config/schemas/config.schemas.js';
import { publishedVehicleResultSchema } from '../../vehicles/schemas/vehicles.schemas.js';
export const MIN_COMPARE_VEHICLES = 2;
export const MAX_COMPARE_VEHICLES = 4;
/**
 * Two to four vehicles, each named once. A comparison of one vehicle has nothing to compare, and past
 * four the result panel and the report's spec cards stop fitting on a page (plan §7, §8).
 */
export const compareVehicleIdsSchema = z
    .array(uuidSchema)
    .min(MIN_COMPARE_VEHICLES, `Pick at least ${MIN_COMPARE_VEHICLES} vehicles`)
    .max(MAX_COMPARE_VEHICLES, `Compare at most ${MAX_COMPARE_VEHICLES} vehicles`)
    .refine((ids) => new Set(ids).size === ids.length, 'Each vehicle can only be compared once');
/** The vehicle facts the compare screen needs to label a column and warn about a cross-segment pick. */
export const compareVehicleRefSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    /** `Toyota Land Cruiser 300 Hybrid 2026` — the label the runs list and the report share. */
    label: z.string().min(1),
    manufacturer: z.string().min(1),
    model: z.string().min(1),
    modelYear: z.number().int(),
    powertrainType: powertrainTypeSchema,
    segmentId: uuidSchema,
    segmentName: z.string().min(1),
});
/** Everything the report's spec cards quote (plan §8), so a comparison is self-contained. */
export const compareVehicleSpecSchema = compareVehicleRefSchema.extend({
    trim: z.string().nullable(),
    engine: z.string().nullable(),
    powerHp: z.number().int().nullable(),
    torqueNm: z.number().int().nullable(),
    transmission: z.string().nullable(),
    drivetrain: z.string().nullable(),
    fuelType: z.string().nullable(),
    defaultFuelGrade: z.string().nullable(),
});
/** The matched conditions both halves of a comparison were measured under. */
export const compareScenarioSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    testType: testTypeSchema,
    season: seasonSchema,
    timeBand: timeBandSchema,
    hvacState: hvacStateSchema,
    routeType: routeTypeSchema.nullable(),
    idleDurationMin: z.number().int().nullable(),
    /** The traffic the scenario expects, `null` when it prescribes none. */
    trafficLevel: trafficLevelSchema.nullable(),
    protocolCode: z.string().min(1),
    protocolVersion: z.number().int().min(1),
    /** `L/100km` for a drive test, `L/h` for an idle one. */
    unit: z.string().min(1),
});
export const commonScenariosInputSchema = z.object({
    vehicleIds: compareVehicleIdsSchema,
});
export const commonScenarioVehicleSchema = z.object({
    vehicleId: uuidSchema,
    /** VALID plus VALID_WITH_WARNING runs — what the run-picker will offer. */
    validRunCount: z.number().int().min(1),
    meanResult: z.number().nullable(),
    unit: z.string().min(1),
});
export const commonScenarioSchema = z.object({
    scenario: compareScenarioSchema,
    perVehicle: z.array(commonScenarioVehicleSchema),
});
export const commonScenariosOutputSchema = z.object({
    vehicles: z.array(compareVehicleRefSchema),
    /** True when the picked vehicles do not share one segment — a warning, never a block (plan §7). */
    crossSegment: z.boolean(),
    scenarios: z.array(commonScenarioSchema),
});
/**
 * Either every valid run of every vehicle, or a hand-picked list per vehicle — the run-picker of plan §7
 * starts from `ALL_VALID` and switches to the explicit form the moment the engineer deselects a run.
 */
export const runSelectionSchema = z.union([z.literal('ALL_VALID'), z.record(uuidSchema, z.array(uuidSchema))]);
export const compareInputSchema = z.object({
    vehicleIds: compareVehicleIdsSchema,
    scenarioId: uuidSchema,
    runSelection: runSelectionSchema.default('ALL_VALID'),
});
/** One selected run, kept in the payload so every figure on the screen traces back to a test. */
export const comparisonRunSchema = z.object({
    id: uuidSchema,
    testDate: z.string(),
    routeCode: z.string().nullable(),
    routeName: z.string().nullable(),
    status: runStatusSchema,
    /** The measured figure in the scenario's unit; `null` only if the result row went missing. */
    result: z.number().nullable(),
    distanceTotalKm: z.number().nullable(),
    fuelConsumedL: z.number().nullable(),
});
/** The aggregation engine's output over the selected runs of one vehicle (plan §6). */
export const comparisonAggregateSchema = z.object({
    runCount: z.number().int().min(0),
    mean: z.number().nullable(),
    min: z.number().nullable(),
    max: z.number().nullable(),
    stddev: z.number().nullable(),
    cv: z.number().nullable(),
});
export const comparisonVehicleSchema = z.object({
    vehicle: compareVehicleSpecSchema,
    aggregate: comparisonAggregateSchema,
    unit: z.string().min(1),
    /** How far below the baseline this vehicle came out, in percent. `0` for the baseline itself. */
    percentDiffVsBaseline: z.number().nullable(),
    isBaseline: z.boolean(),
    runs: z.array(comparisonRunSchema),
    /** WLTP / EPA / GCC figures, labelled and never mixed with measured ones (plan §3.3). */
    publishedResults: z.array(publishedVehicleResultSchema),
});
/** A compatibility check's verdict; `BLOCK` is what makes a comparison NOT_COMPARABLE. */
export const compatibilityOutcomeSchema = z.enum(['PASS', 'WARN', 'BLOCK']);
export const compatibilityVerdictSchema = z.enum(['COMPARABLE', 'COMPARABLE_WITH_WARNING', 'NOT_COMPARABLE']);
export const compatibilityVehicleDetailSchema = z.object({
    vehicleId: uuidSchema,
    /** One entry per selected run, in the order the runs are listed; `null` where nothing was recorded. */
    values: z.array(z.union([z.string(), z.number()]).nullable()),
    /** The distinct recorded values as text — the vehicle's cell in the conditions fact table. */
    distinctValues: z.array(z.string()),
});
export const comparisonCheckSchema = z.object({
    key: z.string().min(1),
    operator: compatibilityOperatorSchema,
    onFail: compatibilityOnFailSchema,
    outcome: compatibilityOutcomeSchema,
    detail: z.object({
        /** The run field the check read, e.g. `conditions.fuel_grade`. */
        field: z.string().min(1),
        unit: z.string().nullable(),
        /** The configured tolerance of a `max_diff` check; `null` for the other operators. */
        threshold: z.number().nullable(),
        /** Largest minus smallest recorded value across every selected run; `null` when not numeric. */
        spread: z.number().nullable(),
        /** True when the runs genuinely disagree, as opposed to a reading simply being missing. */
        mismatch: z.boolean(),
        reason: z.string().nullable(),
        perVehicle: z.array(compatibilityVehicleDetailSchema),
    }),
});
/**
 * The headline sentence's numbers: the vehicle that used the most fuel is the baseline, and the one that
 * used the least is what the sentence is about ("22.6% lower for Land Cruiser Hybrid").
 */
export const comparisonHeadlineSchema = z.object({
    baselineVehicleId: uuidSchema,
    bestVehicleId: uuidSchema,
    percentDiff: z.number().nullable(),
    absoluteDiff: z.number().nullable(),
    unit: z.string().min(1),
});
export const comparisonResultSchema = z.object({
    scenario: compareScenarioSchema,
    unit: z.string().min(1),
    crossSegment: z.boolean(),
    /** In the order the vehicles were asked for, so a deep link always renders the same columns. */
    vehicles: z.array(comparisonVehicleSchema),
    baselineVehicleId: uuidSchema,
    compatibility: z.object({
        verdict: compatibilityVerdictSchema,
        checks: z.array(comparisonCheckSchema),
    }),
    headline: comparisonHeadlineSchema,
    generatedAt: z.string().datetime(),
});
//# sourceMappingURL=compare.schemas.js.map